import { useEffect, useState } from "react";
import { Award, FileCheck2, ImagePlus, Medal, PlusCircle, Zap } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState, StatCard } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { EnergyShopPanel } from "../shop/EnergyShopPanel";
import { useIncentiveActions, useIncentives } from "./useIncentives";
export function EmployeeEnergyPage() {
  const { session } = useAuth();
  const query = useIncentives(),
    actions = useIncentiveActions();
  const [ruleId, setRuleId] = useState("");
  const [evidence, setEvidence] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidencePreview, setEvidencePreview] = useState("");
  const [occurredOn, setOccurredOn] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [notice, setNotice] = useState(""),
    [error, setError] = useState("");
  const [appealReasons, setAppealReasons] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => () => {
    if (evidencePreview) URL.revokeObjectURL(evidencePreview);
  }, [evidencePreview]);
  if (!session || !query.data) return <PageSkeleton />;
  const rules = query.data.rules.filter(
      (x) =>
        x.active &&
        (x.scope === "company" || x.storeId === session.profile.storeId),
    ),
    claims = query.data.claims.filter(
      (x) => x.employeeId === session.profile.id,
    ),
    ledger = query.data.ledger.filter(
      (x) => x.employeeId === session.profile.id,
    ),
    balance = ledger.reduce((n, x) => n + x.changePoints, 0);
  const appealByClaim = Object.fromEntries(
    query.data.appeals.map((item) => [item.claimId, item]),
  );
  const submit = async () => {
    setNotice("");
    setError("");
    try {
      await actions.submitClaim({ ruleId, evidence, occurredOn, file: evidenceFile });
      setEvidence("");
      setEvidenceFile(null);
      setEvidencePreview("");
      setNotice("星能申报已提交，等待店长审批。");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "申报失败");
    }
  };
  const selectEvidence = (file: File | null) => {
    setError("");
    if (evidencePreview) URL.revokeObjectURL(evidencePreview);
    if (!file) {
      setEvidenceFile(null);
      setEvidencePreview("");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"].includes(file.type)) {
      setEvidenceFile(null);
      setEvidencePreview("");
      setError("凭证仅支持 JPG、PNG、WebP 或 HEIC 照片。");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setEvidenceFile(null);
      setEvidencePreview("");
      setError("单张凭证照片不能超过10MB。");
      return;
    }
    setEvidenceFile(file);
    setEvidencePreview(URL.createObjectURL(file));
  };
  const appeal = async (claimId: string) => {
    setNotice("");
    setError("");
    try {
      await actions.submitAppeal({
        claimId,
        reason: appealReasons[claimId] ?? "",
      });
      setNotice("申诉已提交，等待异人复核。");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "申诉失败");
    }
  };
  return (
    <div className="page-stack energy-page">
      <header className="page-heading">
        <div>
          <span>成果被看见，成长有回响</span>
          <h1>星能</h1>
          <p>按公开规则申报工作成果，审批后进入不可覆盖的星能流水。</p>
        </div>
        <StatusBadge tone="success">规则透明</StatusBadge>
      </header>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="stat-grid">
        <StatCard
          label="可用星能"
          value={`${balance}`}
          detail="以流水实时汇总"
        />
        <StatCard
          label="待审批"
          value={`${claims.filter((x) => x.status === "pending").length}`}
          detail="店长异人审批"
        />
        <StatCard
          label="本月获得"
          value={`${ledger.filter((x) => new Date(x.occurredAt).getMonth() === new Date().getMonth() && x.changePoints > 0).reduce((n, x) => n + x.changePoints, 0)}`}
          detail="已审批入账"
        />
      </div>
      <div className="management-grid">
        <Card className="management-card">
          <div className="card-heading">
            <PlusCircle />
            <div>
              <span>成果申报</span>
              <h2>申请星能</h2>
            </div>
          </div>
          <div className="v4-form">
            <label className="v4-field">
              <span>星能规则</span>
              <select
                value={ruleId}
                onChange={(e) => setRuleId(e.target.value)}
              >
                <option value="">请选择</option>
                {rules.map((r) => (
                  <option value={r.id} key={r.id}>
                    {r.name} · +{r.points}
                  </option>
                ))}
              </select>
            </label>
            <label className="v4-field">
              <span>成果日期</span>
              <input
                type="date"
                value={occurredOn}
                onChange={(e) => setOccurredOn(e.target.value)}
              />
            </label>
            <label className="v4-field">
              <span>核验说明</span>
              <textarea
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                placeholder="说明会员、订单或服务成果，便于店长核验"
              />
            </label>
            <label className="v4-field">
              <span>照片凭证（必传 · 最大10MB）</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={(event) => selectEvidence(event.target.files?.[0] ?? null)}
              />
            </label>
            {evidencePreview && (
              <a className="evidence-preview" href={evidencePreview} target="_blank" rel="noreferrer">
                <img src={evidencePreview} alt="待上传星能申报凭证预览" />
                <span><ImagePlus size={15} />点击查看原图 · {evidenceFile?.name}</span>
              </a>
            )}
            <Button onClick={submit} disabled={actions.pending || !ruleId || !evidenceFile}>
              <FileCheck2 size={15} />
              提交申报
            </Button>
          </div>
        </Card>
        <Card className="management-card energy-rules">
          <div className="card-heading">
            <Award />
            <div>
              <span>公开透明</span>
              <h2>当前规则</h2>
            </div>
          </div>
          {rules.map((r) => (
            <article key={r.id}>
              <span>
                <Zap size={15} />
              </span>
              <div>
                <strong>{r.name}</strong>
                <p>{r.description}</p>
              </div>
              <b>+{r.points}</b>
            </article>
          ))}
        </Card>
      </div>
      {query.data.honors.filter((item) => item.employeeId === session.profile.id).length > 0 && <Card className="directory-card honor-wall"><div className="card-heading"><Award /><div><span>被看见的专业与贡献</span><h2>我的荣誉墙</h2></div></div><div className="honor-grid">{query.data.honors.filter((item) => item.employeeId === session.profile.id).map((item) => <article key={item.id}><span>{item.badge}</span><div><strong>{item.title}</strong><p>{item.description}</p><small>{new Date(item.awardedAt).toLocaleDateString("zh-CN")}{item.points > 0 ? ` · 奖励 ${item.points} 星能` : ""}</small></div></article>)}</div></Card>}
      <Card className="directory-card">
        <div className="card-heading">
          <Medal />
          <div>
            <span>申请与入账分离</span>
            <h2>我的星能记录</h2>
          </div>
        </div>
        {claims.length ? (
          <div className="energy-history">
            {claims.map((c) => {
              const r = rules.find((x) => x.id === c.ruleId);
              return (
                <article key={c.id}>
                  <div>
                    <strong>{r?.name}</strong>
                    <p>
                      {c.evidence} · {c.occurredOn}
                    </p>
                    {c.evidencePreviewUrl && (
                      <a className="evidence-preview evidence-preview--compact" href={c.evidencePreviewUrl} target="_blank" rel="noreferrer">
                        <img src={c.evidencePreviewUrl} alt="星能申报照片凭证" loading="lazy" />
                        <span>查看照片凭证</span>
                      </a>
                    )}
                  </div>
                  <b>+{r?.points}</b>
                  <StatusBadge
                    tone={
                      c.status === "approved"
                        ? "success"
                        : c.status === "rejected"
                          ? "danger"
                          : "warning"
                    }
                  >
                    {c.status === "approved"
                      ? "已入账"
                      : c.status === "rejected"
                        ? "已驳回"
                        : "待审批"}
                  </StatusBadge>
                  {c.status === "rejected" && !appealByClaim[c.id] && (
                    <div className="inline-action">
                      <input
                        value={appealReasons[c.id] ?? ""}
                        onChange={(event) =>
                          setAppealReasons({
                            ...appealReasons,
                            [c.id]: event.target.value,
                          })
                        }
                        placeholder="填写申诉说明"
                      />
                      <Button
                        variant="ghost"
                        disabled={
                          actions.pending ||
                          (appealReasons[c.id] ?? "").trim().length < 4
                        }
                        onClick={() => appeal(c.id)}
                      >
                        提交申诉
                      </Button>
                    </div>
                  )}
                  {appealByClaim[c.id] && (
                    <StatusBadge
                      tone={
                        appealByClaim[c.id].status === "approved"
                          ? "success"
                          : appealByClaim[c.id].status === "rejected"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {appealByClaim[c.id].status === "approved"
                        ? "申诉通过并补发"
                        : appealByClaim[c.id].status === "rejected"
                          ? "申诉维持驳回"
                          : "申诉复核中"}
                    </StatusBadge>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <EmptyState
            title="暂无星能申报"
            description="完成符合规则的工作成果后，可在上方提交申报。"
          />
        )}
      </Card>
      <EnergyShopPanel />
    </div>
  );
}
